'use client'

import React, { useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import Link from 'next/link'

import {
  PROMO_BANNER_AMBIENT_CONFETTI,
  PROMO_BANNER_DESKTOP_BURST_COUNT,
  PROMO_BANNER_DESKTOP_MEDIA_QUERY,
  PROMO_BANNER_MOBILE_BURST_COUNT,
  useAmbientConfetti,
} from '@/hooks/useAmbientConfetti'
import { useMessages } from '@/lib/i18n/useMessages'
import type { PromotionBannerUiSlide } from '@/lib/promotion-banner/mapPromotionBannerSlides'
import { SITE_PROMO_CHROME_BOUND_ATTR } from '@/lib/promotion-banner/sitePromoChromeInset'
import {
  getPromotionBannerThemeClasses,
  type PromotionBannerTheme,
} from '@/lib/promotion-banner/promotionBannerTheme'
import { cn } from '@/utilities/ui'

const promoBannerHorizontalInsetClassName = 'px-4'
const promoBannerContentPaddingClassName = 'px-4 py-2 md:px-6 md:py-3.5'
const promoBannerCardClassName =
  'relative z-20 w-full min-w-0 max-w-full overflow-hidden rounded-none'

const promoBannerFloatingMobileCardClassName =
  'rounded-2xl shadow-[0px_8px_24px_0px_rgba(0,20,46,0.18)]'

const promoBannerFloatingDesktopCardClassName =
  'lg:rounded-[10px] lg:shadow-[0px_8px_24px_0px_rgba(0,20,46,0.18)]'

const promoBannerDockedDesktopCardClassName = 'lg:rounded-none lg:shadow-none'

const promoBannerDockMotionClassName =
  'transition-[border-radius,box-shadow] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none'

const promoNavButtonBaseClassName =
  'relative z-10 inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full md:size-10'

const promoDismissButtonBaseClassName =
  'relative z-10 inline-flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full md:size-7'

const promoCtaBaseClassName =
  'relative z-10 inline-flex h-12 min-w-0 items-center justify-center rounded-[10px] px-4 font-outfit text-base font-medium md:h-10 md:px-5'

const promoHeadlineBaseClassName =
  'relative z-10 font-outfit text-base font-medium leading-snug md:font-semibold md:uppercase md:tracking-wide'

function PromoBannerFullWidthFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        'box-border w-full min-w-0 max-w-full overflow-visible',
        promoBannerHorizontalInsetClassName,
      )}
    >
      <div className="relative min-w-0 max-w-full overflow-visible">{children}</div>
    </div>
  )
}

type PromoBannerControlsProps = {
  closeAriaLabel: string
  ctaClassName: string
  ctaHref: string
  ctaLabel: string
  dismissible?: boolean
  headline: string
  headlineClassName: string
  navButtonClassName: string
  nextAriaLabel: string
  onDismiss?: () => void
  onNext: () => void
  onPrevious: () => void
  previousAriaLabel: string
}

function PromoBannerDismissButton({
  ariaLabel,
  navButtonClassName,
  onDismiss,
  variant,
}: {
  ariaLabel: string
  navButtonClassName?: string
  onDismiss: () => void
  variant: 'floating' | 'inline'
}) {
  if (variant === 'inline') {
    return (
      <button
        aria-label={ariaLabel}
        className={cn(promoNavButtonBaseClassName, navButtonClassName, 'pointer-events-auto')}
        onClick={(event) => {
          event.stopPropagation()
          onDismiss()
        }}
        type="button"
      >
        <X aria-hidden className="size-4 md:size-5" />
      </button>
    )
  }

  return (
    <button
      aria-label={ariaLabel}
      className={cn(
        promoDismissButtonBaseClassName,
        'border border-bluetext/80 bg-white/60 text-bluetext backdrop-blur-sm transition-colors hover:bg-white/85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 md:hidden',
        'pointer-events-auto absolute right-0 top-0 z-30 translate-x-1/2 -translate-y-1/2',
      )}
      {...{ [SITE_PROMO_CHROME_BOUND_ATTR]: '' }}
      onClick={(event) => {
        event.stopPropagation()
        onDismiss()
      }}
      type="button"
    >
      <X aria-hidden className="size-3" strokeWidth={3} />
    </button>
  )
}

function PromoBannerControls({
  closeAriaLabel,
  ctaClassName,
  ctaHref,
  ctaLabel,
  dismissible = false,
  headline,
  headlineClassName,
  navButtonClassName,
  nextAriaLabel,
  onDismiss,
  onNext,
  onPrevious,
  previousAriaLabel,
}: PromoBannerControlsProps) {
  const showDesktopDismiss = dismissible && onDismiss

  return (
    <>
      <div className="pointer-events-none relative z-10 flex w-full items-center gap-2 md:hidden">
        <button
          aria-label={previousAriaLabel}
          className={cn(promoNavButtonBaseClassName, navButtonClassName, 'pointer-events-auto')}
          onClick={onPrevious}
          type="button"
        >
          <ChevronLeft aria-hidden className="size-4" />
        </button>

        <p className={cn('min-w-0 flex-1 text-left', promoHeadlineBaseClassName, headlineClassName)}>
          {headline}
        </p>

        <button
          aria-label={nextAriaLabel}
          className={cn(promoNavButtonBaseClassName, navButtonClassName, 'pointer-events-auto')}
          onClick={onNext}
          type="button"
        >
          <ChevronRight aria-hidden className="size-4" />
        </button>
      </div>

      <div className="relative z-10 mx-auto hidden w-full items-center gap-3 md:flex md:gap-4">
        <button
          aria-label={previousAriaLabel}
          className={cn(promoNavButtonBaseClassName, navButtonClassName)}
          onClick={onPrevious}
          type="button"
        >
          <ChevronLeft aria-hidden className="size-4 md:size-5" />
        </button>

        <p className={cn('min-w-0 flex-1', promoHeadlineBaseClassName, headlineClassName)}>{headline}</p>

        <Link className={cn(promoCtaBaseClassName, ctaClassName)} href={ctaHref}>
          {ctaLabel}
        </Link>

        <button
          aria-label={nextAriaLabel}
          className={cn(promoNavButtonBaseClassName, navButtonClassName)}
          onClick={onNext}
          type="button"
        >
          <ChevronRight aria-hidden className="size-4 md:size-5" />
        </button>

        {showDesktopDismiss ? (
          <PromoBannerDismissButton
            ariaLabel={closeAriaLabel}
            navButtonClassName={navButtonClassName}
            onDismiss={onDismiss}
            variant="inline"
          />
        ) : null}
      </div>
    </>
  )
}

export type PromoBannerProps = {
  dismissible?: boolean
  docked?: boolean
  floatingOnMobile?: boolean
  fullWidth?: boolean
  onDismiss?: () => void
  slides: PromotionBannerUiSlide[]
}

export const PromoBanner: React.FC<PromoBannerProps> = ({
  dismissible = false,
  docked = false,
  floatingOnMobile = false,
  fullWidth = false,
  onDismiss,
  slides,
}) => {
  const messages = useMessages()
  const copy = messages.landing.promoBanner
  const bannerRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [confettiBurstCount, setConfettiBurstCount] = useState(PROMO_BANNER_MOBILE_BURST_COUNT)

  const safeActiveIndex = slides.length > 0 ? Math.min(activeIndex, slides.length - 1) : 0
  const activeSlide = slides[safeActiveIndex]
  const themeClasses = getPromotionBannerThemeClasses(activeSlide?.theme ?? 'turquoise')
  const ctaLabel =
    activeSlide?.kind === 'promotion' ? copy.promotionCtaLabel : copy.ctaLabel

  useEffect(() => {
    const media = window.matchMedia(PROMO_BANNER_DESKTOP_MEDIA_QUERY)
    const updateBurstCount = () => {
      setConfettiBurstCount(
        media.matches ? PROMO_BANNER_DESKTOP_BURST_COUNT : PROMO_BANNER_MOBILE_BURST_COUNT,
      )
    }

    updateBurstCount()
    media.addEventListener('change', updateBurstCount)

    return () => media.removeEventListener('change', updateBurstCount)
  }, [])

  useEffect(() => {
    if (activeIndex >= slides.length && slides.length > 0) {
      setActiveIndex(0)
    }
  }, [activeIndex, slides.length])

  useAmbientConfetti(bannerRef, {
    ...PROMO_BANNER_AMBIENT_CONFETTI,
    burstCount: confettiBurstCount,
  })

  if (!activeSlide) {
    return null
  }

  const goToPrevious = () => {
    setActiveIndex((index) => (index === 0 ? slides.length - 1 : index - 1))
  }

  const goToNext = () => {
    setActiveIndex((index) => (index === slides.length - 1 ? 0 : index + 1))
  }

  const bannerCard = (
    <div
      ref={bannerRef}
      aria-live="polite"
      {...{ [SITE_PROMO_CHROME_BOUND_ATTR]: '' }}
      className={cn(
        promoBannerCardClassName,
        floatingOnMobile && promoBannerFloatingMobileCardClassName,
        floatingOnMobile &&
          (docked ? promoBannerDockedDesktopCardClassName : promoBannerFloatingDesktopCardClassName),
        floatingOnMobile && promoBannerDockMotionClassName,
        'relative',
        themeClasses.backgroundClassName,
        promoBannerContentPaddingClassName,
      )}
    >
      <Link
        aria-label={activeSlide.title}
        className={cn(
          'absolute inset-0 z-0 md:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          themeClasses.focusRingClassName,
        )}
        href={activeSlide.href}
      />
      <PromoBannerControls
        closeAriaLabel={messages.common.close}
        ctaClassName={themeClasses.ctaClassName}
        ctaHref={activeSlide.href}
        ctaLabel={ctaLabel}
        dismissible={dismissible}
        headline={activeSlide.title}
        headlineClassName={themeClasses.headlineClassName}
        navButtonClassName={themeClasses.navButtonClassName}
        nextAriaLabel={copy.nextAriaLabel}
        onDismiss={onDismiss}
        onNext={goToNext}
        onPrevious={goToPrevious}
        previousAriaLabel={copy.previousAriaLabel}
      />
    </div>
  )

  const banner =
    dismissible && onDismiss ? (
      <div className="relative w-full overflow-visible">
        {bannerCard}
        <PromoBannerDismissButton
          ariaLabel={messages.common.close}
          onDismiss={onDismiss}
          variant="floating"
        />
      </div>
    ) : (
      bannerCard
    )

  return (
    <div
      aria-label={copy.regionAriaLabel}
      className="relative w-full overflow-visible"
      role="region"
    >
      {fullWidth ? <PromoBannerFullWidthFrame>{banner}</PromoBannerFullWidthFrame> : banner}
    </div>
  )
}

export type { PromotionBannerTheme }
